import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
    Package,
    Copy,
    Check,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Link as LinkIcon,
    Globe,
    CreditCard
} from 'lucide-react';
import { generateTrackingUrl, LANGUAGES, CURRENCIES } from '../../utils/tracking';

interface Product {
    id: string;
    name: string;
    description?: string;
    default_commission: number;
    commission_type?: 'percentage' | 'fixed';
    currency: string;
    landing_page_url: string;
    events?: Record<string, { type: 'percentage' | 'fixed'; value: number }>;
    targeting_rules?: {
        allowed_countries?: string[];
        allowed_devices?: string[];
        allowed_os?: string[];
        fallback_url?: string;
    };
    landing_pages?: { id: string; name: string; url: string }[];
    creatives?: {
        landing_pages?: { id: string; name: string; url: string }[];
        banners?: { id: string; name: string; url: string; dimensions?: string }[];
        email_templates?: { id: string; name: string; subject: string; preview_url?: string }[];
    };
}

export default function AffiliateAssets() {
    const { user } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);

    // Selection state
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [selectedLandingPage, setSelectedLandingPage] = useState<string>('default');
    const [clickIdParam, setClickIdParam] = useState('');
    const [subIdParam, setSubIdParam] = useState('');
    const [sourceIdParam, setSourceIdParam] = useState('');

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const productsQuery = query(
                collection(db, 'products'),
                where('active', '==', true)
            );
            const snapshot = await getDocs(productsQuery);
            const productsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Product[];
            setProducts(productsList);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleProduct = (productId: string) => {
        if (expandedProduct === productId) {
            setExpandedProduct(null);
        } else {
            setExpandedProduct(productId);
            // Reset to defaults
            setSelectedLanguage('en');
            setSelectedCurrency('USD');
            setSelectedLandingPage('default');
            setClickIdParam('');
            setSubIdParam('');
            setSourceIdParam('');
        }
    };

    const handleGenerateUrl = (product: Product) => {
        return generateTrackingUrl({
            baseUrl: product.landing_page_url,
            affiliateId: user?.uid || '',
            language: selectedLanguage,
            currency: selectedCurrency,
            productId: product.id,
            landingPageId: selectedLandingPage,
            creatives: product.creatives,
            landing_pages: product.landing_pages,
            clickId: clickIdParam,
            subId: subIdParam,
            sourceId: sourceIdParam
        });
    };

    const copyToClipboard = async (text: string, id: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(id);
            setTimeout(() => setCopied(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-white">Products & Links</h1>
                <p className="text-gray-400">Get tracking codes for available products</p>
            </div>

            {/* Info Box */}
            <div className="card bg-linear-to-r from-primary-500/10 to-accent-500/10 border-primary-500/20">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0">
                        <LinkIcon className="text-primary-400" size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-white mb-1">Direct Linking Tracking</h3>
                        <p className="text-gray-400 text-sm mb-3">
                            We use direct linking - send traffic directly using the generated URLs below. You can append your own tracking macros for click IDs, sub IDs, and source IDs.
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2 text-emerald-400">
                                <Check size={16} />
                                <span>No redirect delays</span>
                            </div>
                            <div className="flex items-center gap-2 text-emerald-400">
                                <Check size={16} />
                                <span>Server-to-server tracking compatible</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Products List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : products.length === 0 ? (
                <div className="card text-center py-12">
                    <Package className="mx-auto text-gray-500 mb-4" size={48} />
                    <h3 className="text-lg font-semibold text-white mb-2">No Products Available</h3>
                    <p className="text-gray-400">Contact your administrator to get access to products.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {products.map((product) => {
                        const isExpanded = expandedProduct === product.id;
                        const trackingUrl = handleGenerateUrl(product);

                        // Combine top-level landing pages and legacy creative landing pages
                        const availableLandingPages = [
                            ...(product.landing_pages || []),
                            ...(product.creatives?.landing_pages || [])
                        ];

                        return (
                            <div key={product.id} className="card">
                                {/* Product Header */}
                                <div
                                    className="flex items-center justify-between cursor-pointer"
                                    onClick={() => toggleProduct(product.id)}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-linear-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                                            <Package className="text-white" size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white">{product.name}</h3>
                                            <p className="text-sm text-gray-400">{product.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex flex-wrap gap-1">
                                            {Object.keys(product.events || {}).length > 0 ? (
                                                Object.entries(product.events!).map(([evt, rate]) => (
                                                    <span key={evt} className="badge badge-success">
                                                        {evt}: {rate.value}{rate.type === 'percentage' ? '%' : ' ' + product.currency}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="badge badge-success">
                                                    {product.default_commission}{product.commission_type === 'percentage' ? '%' : ' ' + product.currency} commission
                                                </span>
                                            )}
                                        </div>
                                        {isExpanded ? (
                                            <ChevronUp className="text-gray-400" size={20} />
                                        ) : (
                                            <ChevronDown className="text-gray-400" size={20} />
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div className="mt-6 pt-6 border-t border-white/10 space-y-6">
                                        {/* Configuration - Language & Currency */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    <Globe size={16} className="inline mr-2" />
                                                    Language
                                                </label>
                                                <select
                                                    value={selectedLanguage}
                                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                                    className="input-field"
                                                >
                                                    {LANGUAGES.map(lang => (
                                                        <option key={lang.code} value={lang.code}>
                                                            {lang.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    <CreditCard size={16} className="inline mr-2" />
                                                    Currency
                                                </label>
                                                <select
                                                    value={selectedCurrency}
                                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                                    className="input-field"
                                                >
                                                    {CURRENCIES.map(curr => (
                                                        <option key={curr.code} value={curr.code}>
                                                            {curr.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="md:col-span-2 lg:col-span-1">
                                                {availableLandingPages.length > 0 && (
                                                    <>
                                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                                            <ExternalLink size={16} className="inline mr-2" />
                                                            Entry Link
                                                        </label>
                                                        <select
                                                            value={selectedLandingPage}
                                                            onChange={(e) => setSelectedLandingPage(e.target.value)}
                                                            className="input-field"
                                                        >
                                                            <option value="default">Default Product Page</option>
                                                            {availableLandingPages.map(lp => (
                                                                <option key={lp.id} value={lp.id}>
                                                                    {lp.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* URL Parameters Fields */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    Click ID (Macro)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={clickIdParam}
                                                    onChange={e => setClickIdParam(e.target.value)}
                                                    placeholder="{click.id}"
                                                    className="input-field"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    Sub ID (Macro)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={subIdParam}
                                                    onChange={e => setSubIdParam(e.target.value)}
                                                    placeholder="{sub.id}"
                                                    className="input-field"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    Source ID (Macro)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={sourceIdParam}
                                                    onChange={e => setSourceIdParam(e.target.value)}
                                                    placeholder="{source.id}"
                                                    className="input-field"
                                                />
                                            </div>
                                        </div>

                                        {/* Landing Page URL */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                <ExternalLink size={16} className="inline mr-2" />
                                                Your Tracking Link
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={trackingUrl}
                                                    readOnly
                                                    className="input-field flex-1 text-sm font-mono bg-gray-900/50"
                                                />
                                                <button
                                                    onClick={() => copyToClipboard(trackingUrl, `url-${product.id}`)}
                                                    className="btn-secondary px-4 flex items-center gap-2"
                                                >
                                                    {copied === `url-${product.id}` ? (
                                                        <Check size={18} className="text-emerald-400" />
                                                    ) : (
                                                        <Copy size={18} />
                                                    )}
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">
                                                Copy and use this link directly in your campaigns.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
